import React, { useState } from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTheme } from 'next-themes';
import Skeleton from 'react-loading-skeleton';

import { Button } from '@/components/ui';
import { ETheme } from '@/enums/theme';

import styles from './EshopItems.module.scss';

const EshopItems = ({ product }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { theme } = useTheme();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const months = product?.instalment_payment?.months;
  const destinationUrl = router.query.webview
    ? `${product?.slug}?webview=${router.query.webview}`
    : product?.slug;

  const monthlyPayment = () => {
    const price = (product?.new_price / months?.[months?.length - 1]).toFixed(
      2,
    );
    return price;
  };
  const handleButtonAction = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: product?.currency,
        items: [
          {
            item_id: product?.id,
            item_name: product?.name,
            price: product?.new_price ?? product?.old_price,
            quantity: 1,
          },
        ],
      },
    });
    router.push(destinationUrl);
  };
  const renderLabels = (item) => {
    return (
      <div
        key={item?.id}
        className={classNames([
          styles.small_box,
          { [styles.dark]: item?.color === 'deep_red' },
        ])}
        itemScope
        itemType='https://schema.org/Thing'
      >
        {item?.icon && item?.dark_mode_icon && (
          <Image
            src={theme === ETheme.DARK_MODE ? item.dark_mode_icon : item.icon}
            alt='label icon'
            width={20}
            height={20}
            loading='lazy'
          />
        )}
        <span
          className={styles.desc}
          dangerouslySetInnerHTML={{
            __html: item?.title?.replace(/\\n/g, '<br>'),
          }}
        />
      </div>
    );
  };

  return (
    <div
      className={styles.product_wrapper}
      itemScope
      itemType='https://schema.org/ListItem'
    >
      <div>
        <div className={styles.img_box}>
          <Link href={product?.slug}>
            <a>
              {!isImageLoaded && (
                <Skeleton
                  width={250}
                  height={250}
                  style={{ position: 'absolute', top: '24px' }}
                />
              )}
              <Image
                src={product?.album?.photos?.[0]}
                alt={product?.name}
                title={product?.name}
                width={250}
                height={250}
                onLoadingComplete={() => setIsImageLoaded(true)}
                style={{
                  visibility: isImageLoaded ? 'visible' : 'hidden',
                }}
                loading='lazy'
              />
            </a>
          </Link>
        </div>
        <div className={styles.content_box}>
          <Link href={product?.slug}>
            <a>
              <p className={styles.product_title}>{product?.name}</p>
              <div className={styles.price_box}>
                <div className={styles.price}>
                  <span>{product?.new_price}</span>
                  <span className={styles.currency}>{product?.currency}</span>
                </div>
                {product?.old_price && (
                  <div className={styles.old_price}>
                    <span>{product?.old_price}</span>
                    <span>₼</span>
                  </div>
                )}
              </div>
              <div className={styles.monthly_box}>
                {product?.instalment_payment?.icon && (
                  <div className={styles.icon_box}>
                    <Image
                      src={product?.instalment_payment?.icon}
                      alt='birbank icon'
                      title='Birbank Icon'
                      width={20}
                      height={20}
                      className={styles.icon}
                      loading='lazy'
                    />
                  </div>
                )}
                <div className={styles.label_wrapper}>
                  {product?.labels?.map(renderLabels)}
                </div>
                <div className={styles.payment_container}>
                  <span>{monthlyPayment()}</span>
                  <span>₼</span>
                  <span className={styles.item}>x</span>
                  <span>
                    {months?.[months?.length - 1]}
                    {product?.instalment_payment?.unit}
                  </span>
                  <span className={styles.item}>
                    {product?.monthly?.paymentText}
                  </span>
                </div>
              </div>
            </a>
          </Link>
        </div>
      </div>
      <Button onClick={handleButtonAction} className={styles.btn}>
        {t('device:common.details')}
      </Button>
    </div>
  );
};

export default EshopItems;
